import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactResizeDetector from "react-resize-detector";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconMove } from "../../../../icons/move.svg";
import { ReactComponent as IconTrash } from "../../../../icons/trash.svg";
import useElementClientBoundingRect from "../../../../lib/useElementClientBoundingRect";
import { OrganizedMode } from "../../../../states/customization";
import { getPpmm, getZoomLimitPageSizes } from "../../../../utils/customization/zoom";
import EditablePageList from "../../../molecules/customization/EditablePageList";
import TranslationByType, {
  getTranslationByType,
} from "../../../molecules/customization/TranslationByType";
import ValidationHeader from "../../../molecules/customization/ValidationHeader";
import ConfirmChangesModal from "../../../molecules/modals/ConfirmChangesModal";
import "./style.scss";

const DEFAULT_PPMM = 1;
const DEFAULT_ZOOM_LEVEL = 1;
const MIN_BOTTOM_BAR_HEIGHT = 58;
const MARGIN_HEIGHT = 32 + 22 - MIN_BOTTOM_BAR_HEIGHT; // margin + pageCount
const MARGIN_WIDTH = 0;
const ITEMS_MARGIN_WIDTH = 32;

interface Props {
  mode: OrganizedMode;
}

const PageListOrganization: FunctionComponent<Props> = ({ mode }) => {
  const intl = useIntl();
  const { configuration, content, dispatch, template, undoRedo } = useCustomizationContext();
  const [ppmm, setPpmm] = useState<number>(DEFAULT_PPMM);
  const [removablePageKeys, setRemovablePageKeys] = useState<string[]>([]);

  const zoomLimitPageSizes = getZoomLimitPageSizes(DEFAULT_ZOOM_LEVEL, content.templateType);
  const pageMessageRef = useRef<HTMLDivElement>(null);
  const pageMessageHeight = pageMessageRef.current ? pageMessageRef.current.offsetHeight : 40;
  const pageListRef = useRef<HTMLDivElement>(null);
  const containerRect = useElementClientBoundingRect(pageListRef);

  const maxPageHeight = useMemo(() => {
    return Math.max(
      ...template.pageDefinitions.map(({ height }) => height.value),
      ...template.presentationDefinitions.map(({ height }) => height.value)
    );
  }, [template.pageDefinitions, template.presentationDefinitions]);

  const maxPageWidth = useMemo(() => {
    return Math.max(
      ...template.pageDefinitions.map(({ width }) => width.value),
      ...template.presentationDefinitions.map(({ width }) => width.value)
    );
  }, [template.pageDefinitions, template.presentationDefinitions]);

  const onResize = useCallback(
    (width: number | undefined, height: number | undefined) => {
      if (!maxPageHeight || !maxPageWidth || !width || !height) {
        setPpmm(DEFAULT_PPMM);
        return;
      }

      setPpmm(
        getPpmm(
          template,
          DEFAULT_ZOOM_LEVEL,
          width,
          height,
          MARGIN_WIDTH,
          MARGIN_HEIGHT,
          ITEMS_MARGIN_WIDTH,
          maxPageWidth,
          maxPageHeight,
          zoomLimitPageSizes
        )
      );
    },
    [maxPageHeight, maxPageWidth, setPpmm, template, zoomLimitPageSizes]
  );

  useEffect(() => {
    if (containerRect) {
      onResize(containerRect.width, containerRect.height - pageMessageHeight);
    }
  }, [containerRect, onResize, pageMessageHeight, zoomLimitPageSizes]);

  const handleOnValidate = () => {
    if (mode === "deletion" && !displayRemoveModal) {
      setDisplayRemoveModal(true);
    } else {
      dispatch({ type: "setOrganizePageList", opened: false, mode });
    }
  };

  const cancelReorder = useCallback(() => {
    if (undoRedo.lastUndoKey && undoRedo.lastUndoKey.indexOf("changePageOrder") === 0) {
      dispatch({ type: "undoRedo", way: "undo" });
    }
    dispatch({ type: "setOrganizePageList", opened: false, mode });
  }, [dispatch, mode, undoRedo]);

  const handleOnCancel = () => {
    if (mode === "deletion" && !displayRemoveModal) {
      setRemovablePageKeys([]);
      dispatch({ type: "setOrganizePageList", opened: false, mode });
    } else {
      cancelReorder();
    }
  };

  const removePages = () => {
    if (removablePageKeys.length === nbPack) {
      dispatch({
        options: configuration?.options,
        pageKeys: removablePageKeys,
        type: "removeSelectedPages",
      });
    }
    setRemovablePageKeys([]);
    setDisplayRemoveModal(false);
    dispatch({ type: "setOrganizePageList", opened: false, mode });
  };

  const nbPack = configuration?.pages?.increment;
  const removeLabel = intl.formatMessage({
    id: "kustom_PageListOrganization_remove_modal_button_ok",
  });
  const removeFeedBack = intl.formatMessage(
    { id: "kustom_PageListOrganization_selected" },
    { count: removablePageKeys.length, total: nbPack }
  );
  const isRemoveButtonDisabled = !nbPack || removablePageKeys.length !== nbPack;

  const [displayRemoveModal, setDisplayRemoveModal] = useState<boolean>(false);
  const removeModal =
    mode === "deletion" ? (
      <ConfirmChangesModal
        confirmLabel={intl.formatMessage({
          id: "kustom_PageListOrganization_remove_modal_button_ok",
        })}
        dismissLabel={intl.formatMessage({ id: "kustom_ConfirmModal_cancel" })}
        display={displayRemoveModal}
        mode="error"
        onConfirm={removePages}
        onDismiss={() => {
          setDisplayRemoveModal(false);
        }}
        text={getTranslationByType(intl, {
          count: nbPack,
          pageName: configuration?.pageName,
          templateType: template.templateType,
          translationBase: "kustom_PageListOrganization_remove_modal_text",
        })}
        title={getTranslationByType(intl, {
          count: nbPack,
          pageName: configuration?.pageName,
          templateType: template.templateType,
          translationBase: "kustom_PageListOrganization_remove_modal_title",
        })}
      />
    ) : null;

  return (
    <div className="PageListOrganization">
      <div className="PageListOrganization-headingMenu">
        <ValidationHeader
          feedback={
            mode === "deletion"
              ? removeFeedBack
              : intl.formatMessage({ id: "kustom_PageList_Reorder" })
          }
          onCancel={handleOnCancel}
          onValidate={handleOnValidate}
          validateButton={
            mode === "deletion"
              ? {
                  label: removeLabel,
                  type: "error",
                  disabled: isRemoveButtonDisabled,
                }
              : undefined
          }
        />
      </div>

      <div className="PageListOrganization-pageList" ref={pageListRef}>
        <div className="PageListOrganization-pageList-message" ref={pageMessageRef}>
          {mode === "ordering" && template.templateType === "book" && (
            <>
              <IconMove />{" "}
              <FormattedMessage id={`kustom_PageListOrganization_message__${template.pageMode}`} />
            </>
          )}
          {mode === "deletion" && nbPack !== undefined && (
            <>
              <IconTrash />
              <span className="PageListOrganization-pageList-message-text">
                <TranslationByType
                  count={nbPack}
                  pageName={configuration && configuration.pageName}
                  templateType={template.templateType}
                  translationBase="kustom_PageListOrganization_deleteTips"
                />
              </span>
            </>
          )}
        </div>
        {mode === "ordering" ? (
          <EditablePageList organizeMode={mode} ppmm={ppmm} />
        ) : (
          <EditablePageList
            onItemSelected={(values) => {
              setRemovablePageKeys(values);
            }}
            organizeMode={mode}
            ppmm={ppmm}
          />
        )}
        <ReactResizeDetector handleHeight={true} handleWidth={true} onResize={onResize} />
      </div>

      {removeModal}
    </div>
  );
};

export default PageListOrganization;
